import { FrontendMenuItem, MenuItem, MenuItemLinkType, MenuItemWithChildren } from '@/types/menu.types';
import { createClient } from '@/utils/supabase/client';

/**
 * Fetch published menu items for a specific festival and locale
 * Returns hierarchical structure for frontend consumption
 */
export async function getMenuItems(festivalId: string, locale: string = 'fr'): Promise<FrontendMenuItem[]> {
  try {
    const supabase = createClient();
    
    const { data, error } = await supabase
      .from('menu_items')
      .select('*')
      .eq('festival_id', festivalId)
      .eq('is_published', true)
      .order('order_index', { ascending: true });

    if (error) {
      console.error('Error fetching menu items:', error);
      return [];
    }

    if (!data || data.length === 0) {
      return [];
    }

    // Transform flat data into hierarchical structure
    return transformToHierarchicalMenu(data, locale);
  } catch (error) {
    console.error('Error in getMenuItems:', error);
    return [];
  }
}

/**
 * Fetch all menu items for a festival (including unpublished)
 * Used by admin panel
 */
export async function getAllMenuItems(festivalId: string): Promise<MenuItemWithChildren[]> {
  try {
    const supabase = createClient();
    
    const { data, error } = await supabase
      .from('menu_items')
      .select('*')
      .eq('festival_id', festivalId)
      .order('order_index', { ascending: true });

    if (error) {
      console.error('Error fetching all menu items:', error);
      return [];
    }

    if (!data || data.length === 0) {
      return [];
    }

    // Transform flat data into hierarchical structure
    return transformToHierarchicalStructure(data);
  } catch (error) {
    console.error('Error in getAllMenuItems:', error);
    return [];
  }
}

/**
 * Transform flat database rows into hierarchical menu structure for frontend
 */
function transformToHierarchicalMenu(data: MenuItem[], locale: string): FrontendMenuItem[] {
  const menuMap = new Map<string, FrontendMenuItem>();
  const rootItems: FrontendMenuItem[] = [];

  // First pass: create all menu items
  data.forEach(item => {
    const title = getLocalizedTitle(item.title, locale);
    const link = getItemLink(item, locale);

    const menuItem: FrontendMenuItem = {
      title,
      link: item.parent_id ? undefined : link,
      openInNewTab: item.parent_id ? undefined : (item.open_in_new_tab || false),
      items: []
    };

    menuMap.set(item.id, menuItem);
  });

  // Second pass: build hierarchy
  data.forEach(item => {
    const menuItem = menuMap.get(item.id);
    if (!menuItem) return;

    if (item.parent_id) {
      // This is a child item
      const parent = menuMap.get(item.parent_id);
      if (parent) {
        const childLink = getItemLink(item, locale);
        parent.items.push({
          title: menuItem.title,
          link: childLink || '#',
          openInNewTab: item.open_in_new_tab || false
        });
      }
    } else {
      // This is a root item
      rootItems.push(menuItem);
    }
  });

  return rootItems;
}

/**
 * Transform flat database rows into hierarchical structure for admin
 */
function transformToHierarchicalStructure(data: MenuItem[]): MenuItemWithChildren[] {
  const itemMap = new Map<string, MenuItemWithChildren>();
  const rootItems: MenuItemWithChildren[] = [];

  // First pass: create all items
  data.forEach(item => {
    const itemWithChildren: MenuItemWithChildren = {
      ...item,
      children: []
    };
    itemMap.set(item.id, itemWithChildren);
  });

  // Second pass: build hierarchy
  data.forEach(item => {
    const itemWithChildren = itemMap.get(item.id);
    if (!itemWithChildren) return;

    if (item.parent_id) {
      const parent = itemMap.get(item.parent_id);
      if (parent) {
        parent.children!.push(itemWithChildren);
      }
    } else {
      rootItems.push(itemWithChildren);
    }
  });

  return rootItems;
}

/**
 * Get localized title from JSONB title field
 */
function getLocalizedTitle(titleJson: any, locale: string): string {
  if (!titleJson || typeof titleJson !== 'object') {
    return 'Untitled';
  }

  // Try to get the requested locale, fallback to 'en', then 'fr', then any available
  return titleJson[locale] || titleJson['en'] || titleJson['fr'] || Object.values(titleJson)[0] || 'Untitled';
}

/**
 * Get the appropriate link for a menu item based on its type
 */
function getItemLink(item: MenuItem, locale?: string): string | undefined {
  if (item.link_type === MenuItemLinkType.INTERNAL && item.internal_page) {
    return item.internal_page;
  }
  
  if (item.link_type === MenuItemLinkType.CUSTOM && item.custom_url) {
    if (item.custom_url.includes('get-involved')) {
      return locale === 'fr' 
        ? 'https://image-nation.org/fr/get-involved/'
        : 'https://image-nation.org/en/get-involved/';
    }
    return item.custom_url;
  }

  // Return undefined for parent items with no direct links (they should be clickable to expand)
  return undefined;
}

/**
 * Create a new menu item
 */
export async function createMenuItem(menuItem: {
  festival_id: string;
  parent_id?: string;
  title: { en: string; fr: string };
  link_type: MenuItemLinkType;
  internal_page?: string;
  custom_url?: string;
  open_in_new_tab?: boolean;
  order_index: number;
  is_published?: boolean;
}): Promise<MenuItem | null> {
  try {
    const supabase = createClient();
    
    const { data, error } = await supabase
      .from('menu_items')
      .insert(menuItem)
      .select()
      .single();

    if (error) {
      console.error('Error creating menu item:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error in createMenuItem:', error);
    return null;
  }
}

/**
 * Update a menu item
 */
export async function updateMenuItem(
  id: string, 
  updates: {
    parent_id?: string | null;
    title?: { en: string; fr: string };
    link_type?: MenuItemLinkType;
    internal_page?: string | null;
    custom_url?: string | null;
    open_in_new_tab?: boolean;
    order_index?: number;
    is_published?: boolean;
  }
): Promise<MenuItem | null> {
  try {
    const supabase = createClient();
    
    const { data, error } = await supabase
      .from('menu_items')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating menu item:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error in updateMenuItem:', error);
    return null;
  }
}

/**
 * Delete a menu item
 */
export async function deleteMenuItem(id: string): Promise<boolean> {
  try {
    const supabase = createClient();
    
    const { error } = await supabase
      .from('menu_items')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting menu item:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in deleteMenuItem:', error);
    return false;
  }
}

/**
 * Reorder menu items by updating their order_index
 */
export async function reorderMenuItems(festivalId: string, itemOrders: { id: string; order_index: number }[]): Promise<boolean> {
  try {
    const supabase = createClient();
    
    // Update each item's order_index
    const updatePromises = itemOrders.map(({ id, order_index }) =>
      supabase
        .from('menu_items')
        .update({ order_index })
        .eq('id', id)
        .eq('festival_id', festivalId)
    );

    const results = await Promise.all(updatePromises);
    
    // Check if any updates failed
    const hasErrors = results.some(result => result.error);
    
    if (hasErrors) {
      console.error('Error reordering menu items');
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in reorderMenuItems:', error);
    return false;
  }
}
